import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Plus, X, Pencil, Trash2, ToggleLeft, ToggleRight, Shield, Eye, EyeOff } from "lucide-react";

const ALL_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "products", label: "Produk" },
  { key: "categories", label: "Kategori" },
  { key: "orders", label: "Order" },
  { key: "deposits", label: "Top Up" },
  { key: "withdrawals", label: "Tarik Dana" },
  { key: "users", label: "User" },
  { key: "master_panel", label: "Master Panel" },
];

const emptyForm = { name: "", phone: "", password: "", permissions: [] };

export default function SubAdmins() {
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/admin/sub-admins");
      setAdmins(res.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const togglePermission = (key) => {
    const perms = form.permissions.includes(key)
      ? form.permissions.filter(p => p !== key)
      : [...form.permissions, key];
    setForm({ ...form, permissions: perms });
  };

  const selectAll = () => setForm({ ...form, permissions: ALL_PERMISSIONS.map(p => p.key) });
  const clearAll = () => setForm({ ...form, permissions: [] });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/admin/sub-admins/${editing.id}`, {
          name: form.name,
          permissions: form.permissions,
        });
        toast.success("Admin diperbarui!");
      } else {
        await api.post("/admin/sub-admins", form);
        toast.success("Admin baru berhasil dibuat!");
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const toggleActive = async (admin) => {
    try {
      await api.put(`/admin/sub-admins/${admin.id}`, { is_active: !admin.is_active });
      toast.success(admin.is_active ? "Admin dinonaktifkan" : "Admin diaktifkan");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const deleteAdmin = async (admin) => {
    if (!window.confirm(`Hapus admin ${admin.name}?`)) return;
    try {
      await api.delete(`/admin/sub-admins/${admin.id}`);
      toast.success("Admin dihapus");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const startEdit = (admin) => {
    setEditing(admin);
    setForm({ name: admin.name, phone: admin.phone, password: "", permissions: admin.permissions || [] });
    setShowForm(true);
    setShowPassword(false);
  };

  return (
    <div>
      <div className="label-mono text-zinc-500">// MASTER PANEL</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Kelola Admin</h1>
      <p className="font-mono text-xs text-zinc-500 mt-1">Buat dan atur akses panel admin untuk tim kamu.</p>

      <div className="mt-6 flex justify-between items-center">
        <div className="font-mono text-xs text-zinc-500">{admins.filter(a => a.role === "sub_admin").length} sub-admin terdaftar</div>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(emptyForm); setShowPassword(false); }}
          className="btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Buat Admin Baru
        </button>
      </div>

      {/* FORM BUAT/EDIT ADMIN */}
      {showForm && (
        <form onSubmit={submit} className="mt-4 border border-black bg-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="label-mono text-zinc-500">{editing ? "// EDIT ADMIN" : "// BUAT ADMIN BARU"}</div>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="label-mono block mb-2">Nama Admin</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-brutal" placeholder="contoh: Nanda" required />
            </div>
            <div>
              <label className="label-mono block mb-2">No. Handphone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-brutal" placeholder="08xxxxxxxxxx" required={!editing} disabled={!!editing} />
            </div>
            <div>
              <label className="label-mono block mb-2">{editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password"}</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-brutal w-full pr-12" placeholder="Min. 6 karakter" required={!editing} minLength={editing ? 0 : 6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="label-mono">Akses Menu</label>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="font-mono text-[10px] underline">Pilih Semua</button>
                <span className="text-zinc-300">|</span>
                <button type="button" onClick={clearAll} className="font-mono text-[10px] underline">Hapus Semua</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ALL_PERMISSIONS.map(p => (
                <label key={p.key} className={`flex items-center gap-2 p-3 border cursor-pointer font-mono text-xs font-bold ${form.permissions.includes(p.key) ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}>
                  <input type="checkbox" checked={form.permissions.includes(p.key)}
                    onChange={() => togglePermission(p.key)} className="hidden" />
                  <Shield className="w-3 h-3" /> {p.label}
                </label>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? "// SAVING..." : editing ? "Update Admin" : "Buat Admin →"}
          </button>
        </form>
      )}

      {/* LIST ADMIN */}
      <div className="mt-6 space-y-3">
        {admins.filter(a => a.role === "sub_admin").length === 0 && (
          <div className="border border-dashed border-zinc-300 p-8 text-center font-mono text-xs text-zinc-500">
            // Belum ada sub-admin · klik "Buat Admin Baru" untuk mulai
          </div>
        )}
        {admins.filter(a => a.role === "sub_admin").map(admin => (
          <div key={admin.id} className={`border border-black bg-white p-4 ${!admin.is_active ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap justify-between items-start gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-lg font-black uppercase">{admin.name}</div>
                  <span className={`badge-status ${admin.is_active ? "bg-[#00C853] text-white" : "bg-zinc-400 text-white"}`}>
                    {admin.is_active ? "AKTIF" : "NONAKTIF"}
                  </span>
                </div>
                <div className="font-mono text-xs text-zinc-500 mt-1">{admin.phone}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(admin.permissions || []).length === 0 ? (
                    <span className="font-mono text-[10px] text-zinc-400">// tidak ada akses</span>
                  ) : (
                    (admin.permissions || []).map(p => {
                      const perm = ALL_PERMISSIONS.find(x => x.key === p);
                      return (
                        <span key={p} className="badge-status bg-black text-white text-[10px]">
                          {perm?.label || p}
                        </span>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleActive(admin)}
                  className="p-2 border border-black hover:bg-zinc-100" title={admin.is_active ? "Nonaktifkan" : "Aktifkan"}>
                  {admin.is_active ? <ToggleRight className="w-4 h-4 text-[#00C853]" /> : <ToggleLeft className="w-4 h-4 text-zinc-400" />}
                </button>
                <button onClick={() => startEdit(admin)} className="p-2 border border-black hover:bg-black hover:text-white" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteAdmin(admin)} className="p-2 border border-black hover:bg-[#FF3333] hover:text-white" title="Hapus">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-2 font-mono text-[10px] text-zinc-400">
              Dibuat: {new Date(admin.created_at).toLocaleString("id-ID")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}